import { ChangeDetectionStrategy, Component, Inject, Input, OnInit, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GoCdkPopoverDirective } from '@go-modules/directives/go-cdk-popover/go-cdk-popover.directive';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { ActivityTemplateService } from '../activity-template/activity-template.service';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ActivityTemplate } from '@go-modules/interfaces/activity';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { SourceTypesLookup } from '@go-modules/interfaces/source-type-lookup';
import { TitleDescription } from '@go-modules/interfaces/title-description';
import { activityTemplateModelToken } from 'go-modules/models/activity-template/activity-template.factory';
import { take } from 'rxjs';
import { EventEmitter } from '@angular/core';

@Component({
	selector: 'activity-type-dropdown',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
		GoCdkPopoverDirective,
		MatSelectModule
	],
	templateUrl: './activity-type-dropdown.component.html',
	styleUrls: ['./activity-type-dropdown.component.scss'],
	providers: [ActivityTemplateService],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityTypeDropdownComponent implements OnInit {
	@Input() public activity: ActivityModel;
	@Input() public template;
	@Output() public onTemplateSelect = new EventEmitter();

	public templates$ = this.activityTemplateService.getTemplates();
	public isVideoShareEnabled: boolean;
	public sourceTypesLookup: SourceTypesLookup;
	public sourceTypes: TitleDescription[];
	public sourceTypeControl = new FormControl<string | null>(null);
	private templates: any;

	constructor (
		public activityFormService: ActivityFormService,
		private activityTemplateService: ActivityTemplateService,
		@Inject(userServiceToken) public userService: UserService,
		@Inject(activityTemplateModelToken) private ActivityTemplateModel
	) {}

	public ngOnInit (): void {
		this.templates$.pipe(take(1)).subscribe((templates) => {
			this.templates = templates;

			const templateId = this.activity?.activity_template_id ??
				templates.find((template) => template.slug === 'standard').id;
			const matchedTemplate = this.findTemplateById(templateId);

			const selectedTemplate = this.ActivityTemplateModel.model(matchedTemplate);

			this.onTemplateSelect.emit(selectedTemplate);
		});

		const activityTemplateIdControl = this.activityFormService.getControl('activity_template_id');

		if (!this.canModifyActivityType()) {
			activityTemplateIdControl.disable();
		} else {
			activityTemplateIdControl.enable();
		}
	}

	public canModifyActivityType (): boolean {
		return !this.activity.hasUsage() || this.activity.isDefault() || this.userService.currentUser.is_root_user;
	}

	public getTemplateType (templates: ActivityTemplate[], id: number | string): string | undefined {
		return templates.find((t) => t.id === id)?.type;
	}

	public onActivityTypeChange (change: MatSelectChange) {
		this.activityFormService.getControl('source_media').setValue(null);

		const matched = this.findTemplateById(change.value);

		const selectedTemplate = matched
			? this.ActivityTemplateModel.model(matched)
			: this.ActivityTemplateModel.model({ id: change.value });

		this.onTemplateSelect.emit(selectedTemplate);
	}

	private findTemplateById (templateId: number) {
		return this.templates.find((t) => t.id === templateId);
	}
}
